import React, { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageTitle from "../components/PageTitle";
import { trackPurchase } from "../utils/analytics";
import { useAuth } from "../context/AuthContext";
import { getOrderDetails } from "../utils/orderApi";

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();

  // Get order details from URL params (if available)
  const orderId = searchParams.get("order_id");
  const orderName = searchParams.get("order_name");
  const orderValue = searchParams.get("value"); // Optional: total value from URL

  // Track purchase event when payment is successful
  useEffect(() => {
    if (orderId) {
      // Try to fetch order details for complete tracking
      const fetchOrderAndTrack = async () => {
        if (token && orderId) {
          try {
            const details = await getOrderDetails(orderId, token);
            const order = Array.isArray(details) ? details[0] : details;
            const items = order?.order_lines || order?.lines || [];
            const value = order?.amount_total || order?.total || orderValue || 0;

            // Map order lines to GA items format
            const gaItems = items.map((line) => ({
              id: String(line.product_id || line.product?.id || ""),
              name: line.product_name || line.name || "",
              price: line.price_unit || line.price || 0,
              qty: line.product_uom_qty || line.quantity || 1,
            }));

            trackPurchase(orderId, gaItems, value);
          } catch (error) {
            // If fetching fails, track with minimal data
            console.warn("Could not fetch order details for tracking:", error);
            trackPurchase(
              orderId,
              [], // Empty items array if we can't fetch
              parseFloat(orderValue) || 0
            );
          }
        } else {
          // Track with minimal data if no token
          trackPurchase(
            orderId,
            [],
            parseFloat(orderValue) || 0
          );
        }
      };

      fetchOrderAndTrack();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]); // Track once when orderId is available

  return (
    <div>
      <Header />
      <PageTitle title="Payment Successful" />

      <section className="flat-spacing-11">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-md-8 col-lg-6">
              <div className="text-center">
                {/* Success Icon */}
                <div className="mb-4">
                  <div
                    style={{
                      width: "120px",
                      height: "120px",
                      borderRadius: "50%",
                      backgroundColor: "#28a745",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                    }}
                  >
                    <svg
                      width="60"
                      height="60"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                {/* Success Message */}
                <h2 className="mb-3" style={{ color: "#28a745" }}>
                  Payment Successful!
                </h2>
                <p className="mb-4 text-muted">
                  Thank you for your purchase. Your order has been confirmed and
                  payment has been processed successfully.
                </p>

                {/* Order Details */}
                {(orderId || orderName) && (
                  <div
                    className="mb-4 p-3"
                    style={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}
                  >
                    <h5 className="mb-2">Order Details</h5>
                    {orderName && (
                      <p className="mb-1">
                        <strong>Order Number:</strong> {orderName}
                      </p>
                    )}
                    {orderId && (
                      <p className="mb-0">
                        <strong>Order ID:</strong> {orderId}
                      </p>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex gap-3 justify-content-center flex-wrap">
                  <button
                    type="button"
                    className="tf-btn btn-line radius-3"
                    onClick={() => navigate("/")}
                  >
                    Continue Shopping
                  </button>
                  <button
                    type="button"
                    className="tf-btn btn-fill radius-3"
                    onClick={() => navigate("/account-orders")}
                  >
                    View Orders
                  </button>
                </div>

                {/* Additional Info */}
                <div
                  className="mt-4 p-3"
                  style={{ backgroundColor: "#e7f3ff", borderRadius: "8px" }}
                >
                  <p className="mb-0 small text-muted">
                    <strong>What's next?</strong> You will receive an email
                    confirmation shortly. You can track your order status in
                    your account.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
